import {
    AccountLicense,
    BASIC_SUBSCRIPTION_CODE,
} from '@experiences/constants';
import type { AccountType } from '@experiences/interfaces';
import {
    useCallback,
    useMemo,
} from 'react';
import { useSelector } from 'react-redux';

import {
    accountType,
    subscriptionCode as subscriptionCodeSelector,
} from '../../store/selectors';
import useIsOnPrem from './useIsOnPrem';

type UpgradeAction = {
    nextTier: keyof typeof AccountLicense | undefined;
    shouldContactSales: boolean;
};

const useCheckLicense = () => {

    const licensePlan = useSelector(accountType);
    const subscriptionCode = useSelector(subscriptionCodeSelector);
    const isOnPrem = useIsOnPrem();

    const includesLicense = useCallback((licenses: AccountLicense[]) => licenses.includes(AccountLicense[licensePlan]), [ licensePlan ]);

    const isOffersRevampAndCommunity = useMemo(() =>
        AccountLicense[licensePlan] <= AccountLicense.FREEKIT && AccountLicense[licensePlan] >= AccountLicense['PRO-TRIAL']
    , [ licensePlan ]);

    const isFreeOrCommunity = useCallback((accountLicense?: AccountType) => accountLicense
        ? AccountLicense[accountLicense] >= AccountLicense.COMMUNITY
        : AccountLicense[licensePlan] >= AccountLicense.COMMUNITY, [ licensePlan ]);

    const isEnterprise = useMemo(() => AccountLicense[licensePlan] === AccountLicense.ENTERPRISE, [ licensePlan ]);

    const isCommunity = useMemo(() => AccountLicense[licensePlan] === AccountLicense.COMMUNITY, [ licensePlan ]);

    const isAccountLicenseEnterpriseOrPro = useCallback((accountLicense?: AccountType) => accountLicense
        ? AccountLicense[accountLicense] === AccountLicense.ENTERPRISE || AccountLicense[accountLicense] === AccountLicense.PRO
        : includesLicense([ AccountLicense.ENTERPRISE, AccountLicense.PRO ]), [ includesLicense ]);

    const isUserEnterprise = useMemo(() => AccountLicense[licensePlan] <= AccountLicense.TRIAL, [ licensePlan ]);

    const isUnlicensed = useMemo(() => includesLicense([ AccountLicense.UNLICENSED ]), [ includesLicense ]);

    const isPro = useMemo(() => includesLicense([ AccountLicense.PRO ]), [ includesLicense ]);

    // The ecom Basic subscription is a PRO plan; only its subscription code tells it apart from legacy PRO.
    const isBasic = useMemo(() => isPro && subscriptionCode?.toUpperCase() === BASIC_SUBSCRIPTION_CODE, [ isPro, subscriptionCode ]);

    const isTrial = useMemo(() => includesLicense([ AccountLicense.TRIAL, AccountLicense['PRO-TRIAL'], AccountLicense['STD-TRIAL'] ]), [ includesLicense ]);

    const canHaveOnlineEnterpriseActivation = useMemo(() => {
        const validAccountTypes = [
            AccountLicense.COMMUNITY,
            AccountLicense.FREEKIT,
            AccountLicense.TRIAL,
            AccountLicense['PRO-TRIAL'],
            AccountLicense['STD-TRIAL'],
            AccountLicense['TEST-TRIAL'],
        ];
        const hasValidLicenseType = validAccountTypes.includes(AccountLicense[licensePlan]);
        return !isOnPrem && (hasValidLicenseType || isPro);
    }, [ isOnPrem, licensePlan, isPro ]);

    const getNextUpgradeTier = useCallback((): UpgradeAction => {
        const currentLicense = AccountLicense[licensePlan] as AccountLicense;

        // Upgrade path
        switch (currentLicense) {
            case AccountLicense['PRO-TRIAL']: // Basic Trial
                return {
                    nextTier: 'STD-TRIAL', // Standard Trial
                    shouldContactSales: false,
                };
            case AccountLicense.FREEKIT:
            case AccountLicense.TRIAL: // Enterprise Trial
            case AccountLicense.COMMUNITY:
            case AccountLicense.PRO:
            case AccountLicense['STD-TRIAL']:
            case AccountLicense['TEST-TRIAL']:
            default:
                return {
                    nextTier: 'ENTERPRISE',
                    shouldContactSales: true,
                };
        }
    }, [ licensePlan ]);

    // Licenses that are not ENTERPRISE, TRIAL, or STD-TRIAL
    const isNonEnterpriseLicense = useMemo(() => AccountLicense[licensePlan] >= AccountLicense['PRO-TRIAL'], [ licensePlan ]);

    return {
        includesLicense,
        isAccountLicenseEnterpriseOrPro,
        isEnterprise,
        isFreeOrCommunity,
        isOffersRevampAndCommunity,
        isUserEnterprise,
        isCommunity,
        isUnlicensed,
        isPro,
        isBasic,
        isTrial,
        canHaveOnlineEnterpriseActivation,
        getNextUpgradeTier,
        isNonEnterpriseLicense,
    };
};

export default useCheckLicense;
